import { FC } from "react";
import { DepositStepHeader } from "./DepositStepHeader";

interface HardwareWalletStepProps {
  /** Whether the user has ticked the acknowledgement. */
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
  onContinue: () => void;
  onBack?: () => void;
}

/**
 * A stop in the deposit wizard, not a notice tucked beside the amount.
 *
 * A hardware wallet completes a deposit and only fails afterwards, at the
 * first trade -- signing is where CIP-8 support runs out, and the deposit path
 * never signs a message. So the cost of finding out late is funds sitting in
 * an account that cannot do anything, which is worth a deliberate step and an
 * explicit acknowledgement.
 */
export const HardwareWalletStep: FC<HardwareWalletStepProps> = ({
  acknowledged,
  onAcknowledgedChange,
  onContinue,
  onBack,
}) => {
  return (
    <>
      <DepositStepHeader title="Before you deposit" />

      <div className="w-full rounded-xl border border-sr-tangerine/40 bg-sr-tangerine/10 p-4 sm:p-5">
        <p className="text-base font-medium text-sr-tangerine">
          Warning: Hardware Wallets not supported yet.
        </p>

        <div className="mt-3 flex flex-col gap-3 text-sm text-sr-neutral-300">
          <p>
            Some hardware wallets have poor support for the CIP-8 signing we
            use for L2 transactions.
          </p>
          <p>
            For the testnet, we recommend not using a hardware wallet. They'll
            be supported better at the mainnet launch, but for now, we
            recommend a software wallet.
          </p>
          <p className="font-medium text-sr-tangerine">
            If you proceed, you may be able to deposit 10 ADA, but might be
            unable to trade or initiate a withdrawal. You'll still receive the
            10 ADA back at the end of the competition.
          </p>
        </div>
      </div>

      <label className="flex w-full cursor-pointer items-start gap-3 text-sm text-sr-neutral-300">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-sr-lemon-muted"
          checked={acknowledged}
          onChange={(e) => onAcknowledgedChange(e.target.checked)}
        />
        <span>
          I understand that a hardware wallet may not be able to trade or
          withdraw during the competition.
        </span>
      </label>

      <div className="flex w-full items-center gap-3">
        {onBack && (
          <button
            type="button"
            className="sr-btn-secondary flex-1"
            onClick={onBack}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="sr-btn-primary flex-1 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!acknowledged}
          onClick={onContinue}
        >
          Continue
        </button>
      </div>
    </>
  );
};
